import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

/**
 * The product panel the search opens when a result row is clicked: the headline block the product screens already
 * show - image, identifiers, costs, availability - and a tab per view of the same product.
 *
 * The panel is handed a whole `productDetail` response and does no loading of its own; picking a distributor and
 * adding it are raised back to the search, which owns the modal and the add emitter.
 */
@Component({
	selector: 'app-product-detail',
	templateUrl: './product-detail.component.html',
	styleUrls: ['./product-detail.component.scss']
})
export class ProductDetailComponent implements OnChanges {

	// Which sections of the payload each tab reads, so the panel knows when its tab is still waiting on one.
	static readonly TAB_SECTIONS = {
		attachments: 'attachments',
		specification: 'specification',
		stock: 'stock',
		customers: 'usage',
		users: 'usage'
	};

	@Input() detail: any = null;
	@Input() loading = false;

	// Whether the screen behind the search can take a product, so the add buttons only appear where they do something.
	@Input() canAdd = false;

	// Organisation alias, for the routes out to a customer or a warehouse.
	@Input() orgAlias = null;

	// Distributor copies already added, as { id, quantity }, so a copy taken from the panel reads the same as one
	// taken from the list.
	@Input() addedProducts: any[] = [];

	// Sections still being fetched, so the tab waiting on one shows a loader instead of "nothing recorded".
	@Input() loadingSections: string[] = [];

	/**
	 * The tab a viewer moved to. Only Suppliers is answered by the panel's own payload; the rest are fetched when
	 * they are opened, so the search asks for them on this.
	 */
	@Output() sectionOpened: EventEmitter<string> = new EventEmitter();

	@Output() closed: EventEmitter<void> = new EventEmitter();
	@Output() add: EventEmitter<any> = new EventEmitter();

	// An accessory is a product in its own right, so clicking one re-opens the panel on it.
	@Output() openProduct: EventEmitter<number> = new EventEmitter();

	// A supplier's row edited from the Suppliers tab. The copy is what gets edited, not the group the panel is on.
	@Output() editProduct: EventEmitter<number> = new EventEmitter();

	activeTab = 'suppliers';

	// The distributor the header card acts against. There is no default distributor in the data - the choice is the
	// user's - so it starts on the copy the panel is headlining, which is the one the results row showed.
	selectedProductId = null;

	// Supplier specifications open one at a time, the way the design shows them.
	openSpecId = null;

	// Which product the panel is currently showing, so a section arriving can be told from a new product opening.
	private loadedProductId = null;

	/**
	 * The lists the panel renders, rebuilt when the payload changes rather than read through a getter.
	 *
	 * A getter hands *ngFor a brand new array on every change-detection pass, so the rows are torn down and
	 * re-created continuously - which drops any text selection the moment it is made, and made the SKU and the
	 * model impossible to copy out of the header.
	 */
	fields: any[] = [];
	specification: any[] = [];
	stockSections: any[] = [];

	tabList = [
		{ id: 'suppliers', title: 'Suppliers' },
		{ id: 'attachments', title: 'Attachments' },
		{ id: 'specification', title: 'Specification' },
		{ id: 'stock', title: 'Stock' },
		{ id: 'customers', title: 'Customers' },
		{ id: 'users', title: 'Users' }
	];

	isAdded(distributor) {
		return (this.addedProducts || []).some(item => item.id === distributor?.product_id);
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (!changes.detail) return;

		// The search replaces the whole payload when a tab's section arrives, so this runs for those too.
		this.buildLists();

		/**
		 * A payload for a different product, rather than one of this product's tabs arriving. The tab stays where
		 * it was - opening an accessory while reading a specification should land on its specification - but its
		 * section came with the product that has just been replaced, so it has to be asked for again.
		 */
		const productId = this.product.product_id ?? null;
		if (productId === this.loadedProductId) return;

		this.loadedProductId = productId;

		/**
		 * Opens on the group's main product - the copy the results row named, imaged and priced - so the panel
		 * doesn't describe one copy in its header while acting against another. Falls back to the top of the
		 * distributor list if the main copy somehow isn't among them.
		 *
		 * Below the check above on purpose: a section arriving replaces the whole payload too, and re-electing
		 * here would throw away the distributor the viewer picked the moment they opened another tab - leaving
		 * "Add to quote" pointing at a different price and pack size with nothing on screen to say so.
		 */
		const main = this.distributors.find(d => d.product_id === this.product.product_id);
		this.selectedProductId = (main || this.distributors[0])?.product_id ?? null;
		this.openSpecId = this.selectedProductId;

		const section = ProductDetailComponent.TAB_SECTIONS[this.activeTab];
		if (section) this.sectionOpened.emit(section);
	}

	/**
	 * The identifier grid beside the image, in the order the product screens list them, plus the two lists that
	 * are a filtered view of what the payload holds.
	 */
	private buildLists(): void {
		this.fields = [
			{ label: 'Manufacturer', value: this.product.manufacturer_name },
			{ label: 'Model', value: this.product.model },
			{ label: 'EAN', value: this.product.ean },
			{ label: 'Manufacturer SKU', value: this.product.manufacturer_sku },
			{ label: 'Product SKU', value: this.product.sku },
			{ label: 'Unit of Measure', value: this.product.unit_name }
		];

		// The union group every distributor copy of this product folds under. The backend only sends it to an
		// elevated role, and only once the analysis has actually matched the product, so its presence is the gate.
		if (this.detail?.union_product_id) {
			this.fields.push({ label: 'WQ ID', value: this.detail.union_product_id });
		}

		this.specification = (this.detail?.specification || []).filter(row => row.value !== null && row.value !== '');

		// Where the product physically sits. The allocations and reservations the same payload carries are left to
		// the stock tab's own panel: this one answers "where is it", not "who has claimed it".
		this.stockSections = (this.detail?.sections || []).filter(section => ['locations', 'serials'].includes(section.id));
	}

	get product() {
		return this.detail?.product || {};
	}

	get costs() {
		return this.detail?.costs || {};
	}

	get canSeeCosts() {
		return !!this.detail?.can_see_costs;
	}

	get distributors() {
		return this.detail?.distributors || [];
	}

	get selected() {
		return this.distributors.find(d => d.product_id === this.selectedProductId) || this.distributors[0] || null;
	}

	get availability() {
		return this.detail?.availability || { on_hand: 0, free: 0, allocated: 0, reserved: 0, warehouse_count: 0 };
	}

	get warehouses() {
		return this.detail?.warehouses || [];
	}

	get accessories() {
		return this.detail?.accessories || [];
	}

	get labour() {
		return this.detail?.labour || [];
	}

	get documents() {
		return this.detail?.documents || [];
	}

	get flags() {
		return this.detail?.flags || [];
	}

	get usageCustomers() {
		return this.detail?.usage?.customers || [];
	}

	get usageUsers() {
		return this.detail?.usage?.users || [];
	}

	get currencyId() {
		return this.costs.currency_id || this.detail?.currency_id || null;
	}

	get title() {
		return [this.product.manufacturer_name, this.product.manufacturer_sku || this.product.model]
			.filter(part => !!part)
			.join(' ');
	}

	get classification() {
		return [this.product.segment, this.product.category, this.product.subcategory].filter(part => !!part).join(' / ');
	}

	selectTab(tab) {
		this.activeTab = tab.id;

		const section = ProductDetailComponent.TAB_SECTIONS[tab.id];
		if (section) this.sectionOpened.emit(section);
	}

	// True while the active tab's data is still on its way, so it shows a loader rather than an empty state.
	get sectionLoading() {
		const section = ProductDetailComponent.TAB_SECTIONS[this.activeTab];
		return !!section && (this.loadingSections || []).indexOf(section) !== -1;
	}

	selectDistributor(distributor) {
		this.selectedProductId = distributor.product_id;
	}

	toggleSpec(distributor) {
		this.openSpecId = this.openSpecId === distributor.product_id ? null : distributor.product_id;
	}

	addDistributor(distributor) {
		this.add.emit(distributor);
	}

	// "1 project · 1 quote · 1 invoice" for a customer's usage - the kinds are named, and only the ones they have.
	customerWhere(customer) {
		const counts = [
			{ count: customer.project_count, labels: ['project', 'projects'] },
			{ count: customer.quote_count, labels: ['quote', 'quotes'] },
			{ count: customer.invoice_count, labels: ['invoice', 'invoices'] }
		];

		const parts = counts
			.filter(part => part.count > 0)
			.map(part => part.count + ' ' + part.labels[part.count === 1 ? 0 : 1]);

		return parts.join(' · ') || 'no open records';
	}

	// "2 quotes · 1 PO" for a user's usage.
	userWhat(user) {
		const labels = { quotes: ['quote', 'quotes'], pos: ['PO', 'POs'], requests: ['request', 'requests'] };

		const parts = Object.keys(labels)
			.filter(key => user[key] > 0)
			.map(key => user[key] + ' ' + labels[key][user[key] === 1 ? 0 : 1]);

		return parts.join(' · ') || 'stock movements only';
	}

	customerLink(customer) {
		if (!this.orgAlias || !customer.id) return null;
		return ['/', this.orgAlias, 'customer', customer.id];
	}

	// A document the product was used on - a quote, or the invoicing or purchase order tab of its project. The
	// backend supplies the segments after the organisation, so the panel doesn't have to know each record's route.
	documentLink(link) {
		if (!link?.length || !this.orgAlias) return null;
		return ['/', this.orgAlias, ...link];
	}

	// The stocked item list for a warehouse, which is the screen that answers "what else is held here".
	warehouseLink(warehouse) {
		if (!this.orgAlias || !warehouse.warehouse_id) return null;
		return ['/', this.orgAlias, 'warehouse', warehouse.warehouse_id, 'your-warehouse', 'stocked-items'];
	}

	// The backend supplies the route segments after the organisation, for both a warehouse block and a line.
	sectionLink(row) {
		if (!row?.link?.length || !this.orgAlias) return null;
		return ['/', this.orgAlias, ...row.link];
	}

	// A second destination for a line whose reference is a record of its own, e.g. the request behind an allocation.
	refLink(row) {
		if (!row?.ref_link?.length || !this.orgAlias) return null;
		return ['/', this.orgAlias, ...row.ref_link];
	}

	/**
	 * "Lutron / AR-M16-DN-AU" for a distributor's own copy, and the codes it lists under beneath it.
	 *
	 * Joined here rather than in the template because a missing manufacturer or model stringified to "null" when
	 * the parts were concatenated inline, and a method returning a string leaves the rows themselves alone - a
	 * getter handing *ngFor a new array on every pass is what made the header's SKU impossible to select.
	 */
	supplierBrand(distributor) {
		return [distributor?.manufacturer_name, distributor?.model].filter(part => !!part).join(' / ');
	}

	supplierCodes(distributor) {
		return [distributor?.manufacturer_sku, distributor?.ean].filter(part => !!part).join(' · ');
	}

	warehouseTypeLabel(warehouse) {
		return (warehouse.type || '').split('_').join(' ');
	}

	// "12 / 4 / 6" - blank where the warehouse has no threshold set rather than a misleading zero.
	thresholds(warehouse) {
		const values = [warehouse.min_qty, warehouse.max_qty, warehouse.reorder_qty];
		if (values.every(value => value === null)) return '';

		return values.map(value => value === null ? '-' : value).join(' / ');
	}

}
